import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { API_URL } from "../lib/utils";
import { PayrollTab } from "../components/hr/PayrollTab";
import { PayslipsTab } from "../components/hr/PayslipsTab";
import { AttendanceTab } from "../components/hr/AttendanceTab";
import axios from "axios";
import { toast } from "sonner";
import { BadgeDollarSign, Clock, CheckCircle2, Palmtree, FileText, XCircle } from "lucide-react";

export default function HRPage() {
  const { token, user, isManager } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [payroll, setPayroll] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vacations, setVacations] = useState([]);
  const [vacBalance, setVacBalance] = useState(null);
  const [vacForm, setVacForm] = useState({ start_date: "", end_date: "", notes: "" });
  const [payslips, setPayslips] = useState([]);
  const [settings, setSettings] = useState({});
  const [attSummary, setAttSummary] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API_URL}/settings`, { headers, withCredentials: true }).then((r) => setSettings(r.data || {})).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const reqs = [
        axios.get(`${API_URL}/hr/attendance?month=${month}`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/hr/vacations`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/hr/my-payslips`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/hr/attendance/summary?month=${month}`, { headers, withCredentials: true }),
      ];
      if (isManager) reqs.push(axios.get(`${API_URL}/hr/payroll?month=${month}`, { headers, withCredentials: true }));
      const results = await Promise.all(reqs);
      setAttendance(results[0].data);
      setVacations(results[1].data.vacations);
      setVacBalance(results[1].data.my_balance);
      setPayslips(results[2].data);
      setAttSummary(results[3].data);
      if (isManager) setPayroll(results[4].data.employees);
    } catch { toast.error("Erro ao carregar dados de RH"); }
    finally { setLoading(false); }
  };

  const requestVacation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/hr/vacations`, vacForm, { headers, withCredentials: true });
      toast.success("Pedido de férias enviado");
      setVacForm({ start_date: "", end_date: "", notes: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no pedido"); }
  };

  const vacAction = async (v, action) => {
    try {
      await axios.put(`${API_URL}/hr/vacations/${v.vacation_id}/${action}`, {}, { headers, withCredentials: true });
      toast.success(action === "approve" ? "Férias aprovadas" : "Férias rejeitadas");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const checkIn = async () => {
    try {
      const r = await axios.post(`${API_URL}/hr/attendance/checkin`, {}, { headers, withCredentials: true });
      toast[r.data.late_minutes > 0 ? "warning" : "success"](r.data.message || "Check-in registado");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no check-in"); }
  };

  const checkOut = async () => {
    try {
      const r = await axios.post(`${API_URL}/hr/attendance/checkout`, {}, { headers, withCredentials: true });
      toast.success(r.data.message || "Check-out registado");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no check-out"); }
  };

  return (
    <div className="space-y-6" data-testid="hr-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Recursos Humanos</h1>
          <p className="text-muted-foreground">Salários, presenças, faltas e atrasos</p>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="hr-month-input" />
      </div>

      <Tabs defaultValue={isManager ? "payroll" : "attendance"}>
        <TabsList className="flex-wrap h-auto">
          {isManager && <TabsTrigger value="payroll" data-testid="tab-payroll"><BadgeDollarSign className="w-4 h-4 mr-1" /> Salários</TabsTrigger>}
          <TabsTrigger value="attendance" data-testid="tab-attendance"><Clock className="w-4 h-4 mr-1" /> Presenças</TabsTrigger>
          <TabsTrigger value="vacations" data-testid="tab-vacations"><Palmtree className="w-4 h-4 mr-1" /> Férias</TabsTrigger>
          <TabsTrigger value="payslips" data-testid="tab-payslips"><FileText className="w-4 h-4 mr-1" /> Meus Recibos</TabsTrigger>
        </TabsList>

        <TabsContent value="vacations" className="mt-4 space-y-4">
          {vacBalance && (
            <Card><CardContent className="p-4 flex gap-6 text-sm">
              <span>Direito anual: <b>{vacBalance.allowance} dias</b></span>
              <span>Usados: <b>{vacBalance.used}</b></span>
              <span>Disponíveis: <b className="text-emerald-600">{vacBalance.remaining}</b></span>
            </CardContent></Card>
          )}
          <form onSubmit={requestVacation} className="flex gap-2 items-end flex-wrap p-3 rounded-lg border">
            <div className="space-y-1"><Label className="text-xs">Início</Label>
              <Input type="date" required value={vacForm.start_date} onChange={(e) => setVacForm({ ...vacForm, start_date: e.target.value })} data-testid="vacation-start-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Fim</Label>
              <Input type="date" required value={vacForm.end_date} onChange={(e) => setVacForm({ ...vacForm, end_date: e.target.value })} data-testid="vacation-end-input" /></div>
            <div className="space-y-1 flex-1 min-w-40"><Label className="text-xs">Notas</Label>
              <Input value={vacForm.notes} onChange={(e) => setVacForm({ ...vacForm, notes: e.target.value })} /></div>
            <Button type="submit" data-testid="request-vacation-btn"><Palmtree className="w-4 h-4 mr-1" /> Pedir Férias</Button>
          </form>
          <Card><CardContent className="p-0 divide-y max-h-96 overflow-y-auto">
            {vacations.length === 0 && <p className="p-4 text-center text-muted-foreground text-sm">Sem pedidos de férias</p>}
            {vacations.map((v) => (
              <div key={v.vacation_id} className="flex justify-between items-center p-3 text-sm gap-2">
                <div>
                  <p className="font-medium">{v.employee_name} · {v.start_date} → {v.end_date} ({v.days} dias)</p>
                  {v.notes && <p className="text-xs text-muted-foreground">{v.notes}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={v.status === "approved" ? "bg-emerald-500/20 text-emerald-600" : v.status === "rejected" ? "bg-red-500/20 text-red-600" : "bg-amber-500/20 text-amber-600"}>
                    {v.status === "approved" ? "Aprovadas" : v.status === "rejected" ? "Rejeitadas" : "Pendente"}
                  </Badge>
                  {isManager && v.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => vacAction(v, "approve")} data-testid={`approve-vacation-${v.vacation_id}`}><CheckCircle2 className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => vacAction(v, "reject")}><XCircle className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="payslips" className="mt-4">
          <PayslipsTab payslips={payslips} settings={settings} />
        </TabsContent>

        {isManager && (
          <TabsContent value="payroll" className="mt-4">
            <PayrollTab payroll={payroll} loading={loading} month={month} headers={headers} settings={settings} onRefresh={fetchAll} />
          </TabsContent>
        )}

        <TabsContent value="attendance" className="mt-4">
          <AttendanceTab attendance={attendance} summary={attSummary} isManager={isManager} headers={headers} onRefresh={fetchAll} onCheckIn={checkIn} onCheckOut={checkOut} userId={user?.user_id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
